import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  PlayCircleIcon,
  ChartBarIcon,
  CubeIcon,
  DocumentTextIcon,
  CogIcon,
  UserGroupIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';

const DemoPage = () => {
  const [activeVideo, setActiveVideo] = useState(0);

  const sections = [
    {
      title: "Project Management",
      description: "Streamline your decking projects from start to finish. See how our intuitive project dashboard helps you manage timelines, resources, and client communications all in one place.",
      videoUrl: "/videos/project-management-demo.mp4",
      icon: DocumentTextIcon
    },
    {
      title: "3D Design Tools",
      description: "Transform your ideas into stunning 3D visualizations. Watch how easily you can create professional deck designs, make real-time adjustments, and impress your clients with photorealistic renderings.",
      videoUrl: "/videos/3d-design-demo.mp4",
      icon: CubeIcon
    },
    {
      title: "Cost Estimation",
      description: "Take the guesswork out of pricing. Learn how our advanced cost estimation tools calculate materials, labor, and overhead to help you create accurate, profitable quotes in minutes.",
      videoUrl: "/videos/estimation-demo.mp4",
      icon: ChartBarIcon
    },
    {
      title: "Material Management",
      description: "Keep track of your inventory and materials with precision. See how our system helps you manage stock levels, automate ordering, and reduce waste.",
      videoUrl: "/videos/inventory-demo.mp4",
      icon: CogIcon
    },
    {
      title: "Client Portal",
      description: "Enhance client communication and satisfaction. Discover how clients can view progress updates, approve designs, and make payments through their dedicated portal.",
      videoUrl: "/videos/client-portal-demo.mp4",
      icon: UserGroupIcon
    },
    {
      title: "Analytics & Reporting",
      description: "Make data-driven decisions for your business. Explore our comprehensive reporting tools that provide insights into project profitability, team performance, and business growth.",
      videoUrl: "/videos/analytics-demo.mp4",
      icon: ChartBarIcon
    }
  ];

  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  const floatingAnimation = {
    y: [-10, 10],
    transition: {
      duration: 2,
      repeat: Infinity,
      repeatType: "reverse",
      ease: "easeInOut"
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-primary-900 overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        {/* Blueprint Grid Background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:30px_30px]" />
          <div className="absolute inset-0 bg-gradient-to-b from-primary-900/50 to-primary-800/50 backdrop-blur-[1px]" />
          
          {/* Floating Elements */}
          <motion.div
            animate={floatingAnimation}
            className="absolute top-20 left-10 w-32 h-32 bg-primary-500/10 rounded-full blur-xl"
          />
          <motion.div
            animate={floatingAnimation}
            className="absolute top-40 right-20 w-40 h-40 bg-secondary-500/10 rounded-full blur-xl"
          />
          <motion.div
            animate={floatingAnimation}
            className="absolute bottom-20 left-1/4 w-36 h-36 bg-blue-500/10 rounded-full blur-xl"
          />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-5xl mx-auto text-center">
            <motion.div
              initial="hidden"
              animate="visible"
              variants={{
                hidden: { opacity: 0, y: 20 },
                visible: {
                  opacity: 1,
                  y: 0,
                  transition: {
                    duration: 0.8,
                    staggerChildren: 0.2
                  }
                }
              }}
            >
              <motion.h1 
                variants={fadeIn}
                className="text-6xl md:text-7xl font-bold text-white mb-8 bg-clip-text text-transparent bg-gradient-to-r from-white via-blue-100 to-white"
              >
                Experience the Future of
                <span className="block text-primary-300">Deck Building</span>
              </motion.h1>
              <motion.p 
                variants={fadeIn}
                className="text-xl md:text-2xl text-blue-100 mb-12 max-w-3xl mx-auto"
              >
                Watch our powerful features in action and discover how Nail the Cost revolutionizes deck construction management
              </motion.p>
              <motion.div 
                variants={fadeIn}
                className="flex flex-col sm:flex-row justify-center gap-6"
              >
                <Link 
                  to="/contact"
                  className="group relative px-8 py-4 bg-gradient-to-r from-primary-500 to-primary-600 rounded-xl font-bold text-lg text-white shadow-lg hover:shadow-primary-500/20 transition-all duration-300 overflow-hidden"
                >
                  <span className="absolute inset-0 bg-gradient-to-r from-primary-400 to-primary-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"/>
                  <span className="relative flex items-center justify-center">
                    Schedule Live Demo
                    <ArrowRightIcon className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform duration-300" />
                  </span>
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Feature Demo Sections */}
      {sections.map((section, index) => (
        <motion.section
          key={index}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="relative py-24"
        >
          {/* Section Background */}
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-grid-white/[0.02] bg-[size:20px_20px]" />
            <div className={`absolute inset-0 ${index % 2 === 0 ? 'bg-primary-900/50' : 'bg-gray-900/50'} backdrop-blur-[1px]`} />
          </div>

          <div className="container mx-auto px-4 relative z-10">
            <div className="grid md:grid-cols-2 gap-16 items-center">
              <motion.div
                variants={fadeIn}
                className={`${index % 2 === 0 ? 'md:order-1' : 'md:order-2'}`}
              >
                <div className="relative group">
                  <div className="absolute -inset-1 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-2xl blur opacity-25 group-hover:opacity-50 transition duration-1000"></div>
                  <div className="relative aspect-video bg-gray-900 rounded-2xl overflow-hidden shadow-2xl">
                    <div className="absolute inset-0 bg-grid-white/[0.02] bg-[size:20px_20px]" />
                    <div className="relative w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-[1px]">
                      <PlayCircleIcon className="w-20 h-20 text-white opacity-80 group-hover:scale-110 transition-transform duration-300" />
                    </div>
                  </div>
                </div>
              </motion.div>
              
              <motion.div
                variants={fadeIn}
                className={`${index % 2 === 0 ? 'md:order-2' : 'md:order-1'}`}
              >
                <div className="flex items-center gap-4 mb-6">
                  <section.icon className="w-12 h-12 text-primary-400" />
                  <h2 className="text-4xl font-bold text-white bg-clip-text text-transparent bg-gradient-to-r from-white to-blue-100">{section.title}</h2>
                </div>
                <p className="text-xl text-blue-100 mb-8">{section.description}</p>
                <button
                  onClick={() => setActiveVideo(index)}
                  className="group relative px-6 py-3 bg-primary-600/20 text-primary-300 rounded-lg hover:bg-primary-600/30 transition-colors duration-300 flex items-center gap-2"
                >
                  <PlayCircleIcon className="w-5 h-5 group-hover:scale-110 transition-transform duration-300" />
                  Watch Demo
                </button>
              </motion.div>
            </div>
          </div>
        </motion.section>
      ))}

      {/* CTA Section */}
      <section className="relative py-24">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:20px_20px]" />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-900/50 to-primary-800/50 backdrop-blur-[1px]" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
            >
              <h2 className="text-5xl font-bold text-white mb-6 bg-clip-text text-transparent bg-gradient-to-r from-white via-blue-100 to-white">
                Ready to Transform Your Business?
              </h2>
              <p className="text-xl text-blue-100 mb-12">
                Join hundreds of successful contractors who trust Nail the Cost
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-6">
                <Link
                  to="/contact"
                  className="group relative px-8 py-4 bg-gradient-to-r from-primary-500 to-primary-600 rounded-xl font-bold text-lg text-white shadow-lg hover:shadow-primary-500/20 transition-all duration-300 overflow-hidden"
                >
                  <span className="absolute inset-0 bg-gradient-to-r from-primary-400 to-primary-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"/>
                  <span className="relative flex items-center justify-center">
                    Schedule Live Demo
                    <ArrowRightIcon className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform duration-300" />
                  </span>
                </Link>
                <Link
                  to="/pricing"
                  className="px-8 py-4 border-2 border-primary-400/30 text-primary-300 hover:bg-primary-400/10 rounded-xl font-bold text-lg transition-all duration-300"
                >
                  View Pricing
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DemoPage; 